import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { organizerListTablesApi, organizerBulkUpsertTablesApi } from "../../api/api";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import OrganizerTimePillSelect from "../components/OrganizerTimePillSelect";
import AvisoSenasBloqueadas from "../components/AvisoSenasBloqueadas";
import useCobrosHabilitados from "../hooks/useCobrosHabilitados";
import {
  SPORTS,
  SPORT_LABELS,
  SURFACES,
  SURFACE_LABELS,
  ENCLOSURES,
  ENCLOSURE_LABELS,
  ALLOWED_DURATIONS,
  DURATION_LABELS,
} from "../../constants/courtDomain";

/* ─────────────────────────────────────────
   CourtLayoutTab — courts + per-court pricing matrix editor for a club
   (Unit 6 / PR6). Persisted via POST /api/organizer/tables/bulk
   (organizerBulkUpsertTablesApi), full-replace-by-name semantics: any
   existing court whose name is absent from the submitted array is
   soft-deleted server-side, sortOrder is the array index. Mirrors the bar
   organizer's "Distribución de mesas" row layout (.zr-zone-row/.zr-add-zone/
   .zr-delete-btn) for the court list, and the shared .zr-table for the
   pricing rules matrix — no new form-control patterns introduced.
───────────────────────────────────────── */

let localIdSeq = 0;
const makeLocalId = () => `court-local-${++localIdSeq}`;

const SPORT_OPTIONS = [{ value: "", label: "Sin definir" }, ...SPORTS.map((s) => ({ value: s, label: SPORT_LABELS[s] || s }))];
const SURFACE_OPTIONS = [{ value: "", label: "Sin definir" }, ...SURFACES.map((s) => ({ value: s, label: SURFACE_LABELS[s] || s }))];
const ENCLOSURE_OPTIONS = [{ value: "", label: "Sin definir" }, ...ENCLOSURES.map((s) => ({ value: s, label: ENCLOSURE_LABELS[s] || s }))];
const DURATION_OPTIONS = ALLOWED_DURATIONS.map((d) => ({ value: String(d), label: DURATION_LABELS[d] || `${d} min` }));
const WEEKDAY_OPTIONS = [
  { value: "", label: "Cualquier día" },
  { value: "0", label: "Domingo" },
  { value: "1", label: "Lunes" },
  { value: "2", label: "Martes" },
  { value: "3", label: "Miércoles" },
  { value: "4", label: "Jueves" },
  { value: "5", label: "Viernes" },
  { value: "6", label: "Sábado" },
];

export function emptyCourt() {
  return {
    localId: makeLocalId(),
    tableId: "",
    name: "",
    zone: "",
    capacity: 4,
    minPartySize: 1,
    status: true,
    sport: "",
    courtCategory: "",
    surface: "",
    lighting: false,
    enclosure: "",
    pricing: { rules: [], defaultByDuration: [] },
  };
}

// deposit === null means "no deposit required for this row" (checkbox unchecked).
function normalizeDepositValue(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// Mirrors the backend guard so the organizer gets the error without a round trip.
export function validateCourtsDeposits(courts) {
  const errors = [];
  (courts || []).forEach((court) => {
    const courtName = String(court.name || "").trim() || "Cancha sin nombre";
    const check = (row, index, sectionLabel) => {
      if (row.deposit === null || row.deposit === undefined || row.deposit === "") return;
      const deposit = Number(row.deposit);
      const price = Number(row.price) || 0;
      if (deposit <= 0) {
        errors.push(`Cancha "${courtName}", ${sectionLabel}, fila ${index + 1}: la seña debe ser mayor que $0.`);
        return;
      }
      if (deposit > price) {
        errors.push(
          `Cancha "${courtName}", ${sectionLabel}, fila ${index + 1}: la seña ($${deposit}) no puede ser mayor que el precio ($${price}).`
        );
      }
    };
    (court.pricing?.defaultByDuration || []).forEach((row, i) => check(row, i, "precio base por duración"));
    (court.pricing?.rules || []).forEach((row, i) => check(row, i, "regla de precio"));
  });
  return errors;
}

function normalizeRule(r) {
  return {
    durationMinutes: Number(r?.durationMinutes) || ALLOWED_DURATIONS[0],
    weekday: r?.weekday === null || r?.weekday === undefined || r?.weekday === "" ? null : Number(r.weekday),
    fromTime: r?.fromTime || "",
    toTime: r?.toTime || "",
    price: Number(r?.price) || 0,
    deposit: normalizeDepositValue(r?.deposit),
  };
}

function normalizeDefault(d) {
  return {
    durationMinutes: Number(d?.durationMinutes) || ALLOWED_DURATIONS[0],
    price: Number(d?.price) || 0,
    deposit: normalizeDepositValue(d?.deposit),
  };
}

export function normalizeCourtsFromApi(list) {
  if (!Array.isArray(list)) return [];
  return list.map((t) => ({
    localId: t._id || makeLocalId(),
    tableId: t._id || "",
    name: t.name || "",
    zone: t.zone || "",
    capacity: Number(t.capacity ?? 4),
    minPartySize: Number(t.minPartySize ?? 1),
    status: t.status !== false,
    sport: t.sport || "",
    courtCategory: t.courtCategory || "",
    surface: t.surface || "",
    lighting: Boolean(t.lighting),
    enclosure: t.enclosure || "",
    pricing: {
      rules: Array.isArray(t.pricing?.rules) ? t.pricing.rules.map(normalizeRule) : [],
      defaultByDuration: Array.isArray(t.pricing?.defaultByDuration)
        ? t.pricing.defaultByDuration.map(normalizeDefault)
        : [],
    },
  }));
}

// sinSenas: sin cobros validados el precio se guarda, pero ninguna fila viaja con seña.
export function buildCourtsPayload(courts, { sinSenas = false } = {}) {
  const sinSena = (row) => (sinSenas ? { ...row, deposit: null } : row);
  return (courts || [])
    .filter((c) => String(c.name || "").trim())
    .map((c) => ({
      name: String(c.name).trim(),
      zone: c.zone || "",
      capacity: Number(c.capacity) || 4,
      minPartySize: Number(c.minPartySize) || 1,
      status: c.status !== false,
      sport: c.sport || "",
      courtCategory: c.courtCategory || "",
      surface: c.surface || "",
      lighting: Boolean(c.lighting),
      enclosure: c.enclosure || "",
      pricing: {
        rules: (c.pricing?.rules || []).map(normalizeRule).map(sinSena),
        defaultByDuration: (c.pricing?.defaultByDuration || []).map(normalizeDefault).map(sinSena),
      },
    }));
}

const DepositCell = ({ row, onChange, rowLabel, bloqueado }) => {
  const enabled = !bloqueado && row.deposit !== null && row.deposit !== undefined && row.deposit !== "";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <input
        type="checkbox"
        className="zr-check"
        aria-label={`Requiere seña — ${rowLabel}`}
        checked={enabled}
        disabled={bloqueado}
        onChange={() => onChange({ deposit: enabled ? null : Number(row.price) || 0 })}
        style={{ margin: 0, cursor: "pointer", accentColor: "var(--zr-lime)" }}
      />
      <input
        type="number"
        min={0}
        className="zr-input"
        aria-label={`Monto de la seña — ${rowLabel}`}
        placeholder="Sin seña"
        disabled={!enabled}
        value={enabled ? row.deposit : ""}
        onChange={(e) => onChange({ deposit: e.target.value === "" ? 0 : Number(e.target.value) })}
        style={enabled ? undefined : { background: "#ffffffc5", cursor: "not-allowed" }}
      />
    </div>
  );
};

const CourtLayoutTab = ({ clubId, courtCategories, sports }) => {
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const loadedForClubId = useRef(null);
  // Mientras carga también bloquea: no se guarda ninguna seña a mitad de camino.
  const { cargando: cargandoCobros, puedeCobrar } = useCobrosHabilitados();
  const senasBloqueadas = cargandoCobros || !puedeCobrar;

  const [expandedCourts, setExpandedCourts] = useState({});
  const [showRulesMap, setShowRulesMap] = useState({});

  const categoryOptions = useMemo(() => {
    const parsed = typeof courtCategories === "string"
      ? courtCategories.split(",").map(s => s.trim()).filter(Boolean)
      : Array.isArray(courtCategories) ? courtCategories : [];
    
    return [
      { value: "", label: "Sin definir" },
      ...parsed.map((cat) => ({ value: cat, label: cat }))
    ];
  }, [courtCategories]);

  const clubSportsOptions = useMemo(() => {
    if (!Array.isArray(sports) || sports.length === 0) {
      return SPORT_OPTIONS;
    }
    return SPORT_OPTIONS.filter((opt) => sports.includes(opt.value));
  }, [sports]);

  const loadCourts = useCallback(async () => {
    if (!clubId) return;
    try {
      setLoading(true);
      setError("");
      const response = await organizerListTablesApi(clubId);
      const list = Array.isArray(response?.data?.info) ? response.data.info : [];
      const normalized = normalizeCourtsFromApi(list);
      setCourts(normalized);

      const initExpanded = {};
      const initRules = {};
      normalized.forEach((c) => {
        if (c.pricing?.rules?.length > 0) {
          initRules[c.localId] = true;
        }
      });
      setExpandedCourts(initExpanded);
      setShowRulesMap(initRules);
    } catch (err) {
      setError(err?.response?.data?.message || "No pudimos cargar las canchas del club.");
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    if (!clubId || loadedForClubId.current === clubId) return;
    loadedForClubId.current = clubId;
    loadCourts();
  }, [clubId, loadCourts]);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const t = setTimeout(() => setToastMessage(""), 3000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  const updateCourt = (localId, patch) =>
    setCourts((prev) => prev.map((c) => (c.localId === localId ? { ...c, ...patch } : c)));

  const updatePricing = (localId, updater) =>
    setCourts((prev) =>
      prev.map((c) => (c.localId === localId ? { ...c, pricing: updater(c.pricing) } : c))
    );

  const handleAddCourt = () => {
    const newC = emptyCourt();
    setCourts((prev) => [...prev, newC]);
    setExpandedCourts((prev) => ({ ...prev, [newC.localId]: true }));
  };

  const handleRemoveCourt = (localId) => setCourts((prev) => prev.filter((c) => c.localId !== localId));

  const handleMoveCourt = (localId, direction) =>
    setCourts((prev) => {
      const idx = prev.findIndex((c) => c.localId === localId);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });

  const handleAddDefaultPrice = (localId) =>
    updatePricing(localId, (pricing) => ({
      ...pricing,
      defaultByDuration: [
        ...pricing.defaultByDuration,
        { durationMinutes: ALLOWED_DURATIONS[0], price: 0, deposit: null },
      ],
    }));

  const handleUpdateDefaultPrice = (localId, idx, patch) =>
    updatePricing(localId, (pricing) => ({
      ...pricing,
      defaultByDuration: pricing.defaultByDuration.map((d, i) => (i === idx ? { ...d, ...patch } : d)),
    }));

  const handleRemoveDefaultPrice = (localId, idx) =>
    updatePricing(localId, (pricing) => ({
      ...pricing,
      defaultByDuration: pricing.defaultByDuration.filter((_, i) => i !== idx),
    }));

  const handleAddRule = (localId) =>
    updatePricing(localId, (pricing) => ({
      ...pricing,
      rules: [
        ...pricing.rules,
        { durationMinutes: ALLOWED_DURATIONS[0], weekday: null, fromTime: "", toTime: "", price: 0, deposit: null },
      ],
    }));

  const handleUpdateRule = (localId, idx, patch) =>
    updatePricing(localId, (pricing) => ({
      ...pricing,
      rules: pricing.rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));

  const handleRemoveRule = (localId, idx) =>
    updatePricing(localId, (pricing) => ({
      ...pricing,
      rules: pricing.rules.filter((_, i) => i !== idx),
    }));

  const handleSave = async (savedName) => {
    if (!clubId) return;
    setError("");
    // Solo se descartan señas con el bloqueo confirmado; mientras carga no se pisa nada.
    const sinSenas = !cargandoCobros && !puedeCobrar;
    const depositErrors = sinSenas ? [] : validateCourtsDeposits(courts);
    if (depositErrors.length > 0) {
      setError(depositErrors.join("\n"));
      return;
    }
    try {
      setSaving(true);
      const tables = buildCourtsPayload(courts, { sinSenas });
      const response = await organizerBulkUpsertTablesApi(clubId, tables);
      const list = Array.isArray(response?.data?.info) ? response.data.info : [];
      const normalized = normalizeCourtsFromApi(list);
      setCourts(normalized);
      setToastMessage("Canchas guardadas correctamente.");
      const savedCourt = savedName ? normalized.find((c) => c.name === savedName) : null;
      if (savedCourt) {
        setExpandedCourts((prev) => ({ ...prev, [savedCourt.localId]: false }));
      }
    } catch (err) {
      setError(err?.response?.data?.message || "No pudimos guardar las canchas.");
    } finally {
      setSaving(false);
    }
  };

  if (!clubId) return null;

  return (
    <div className="zr-panel">
      <div className="zr-court-section-header">
        <div>
          <h2 className="zr-section-head__title">Canchas del club</h2>
          <p className="zr-section-head__hint">| Definí cada cancha y su matriz de precios por duración, día y horario.</p>
        </div>
        <div className="zr-totals">
          <span className="zr-total">
            Total de canchas <b>{String(courts.length).padStart(2, "0")}</b>
          </span>
        </div>
      </div>

      {senasBloqueadas && <AvisoSenasBloqueadas />}

      {error && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #f8b4b4",
            borderRadius: 10,
            padding: "14px 18px",
            marginBottom: 20,
            color: "#e53935",
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: "pre-line",
          }}
        >
          {error}
        </div>
      )}

      {loading && <SpinLoading />}

      {!loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
          {courts.map((court) => {
            const isExpanded = !!expandedCourts[court.localId];
            return (
              <div className="zr-panel" key={court.localId} style={{ background: "#fafafa", padding: "16px 20px" }}>
                <div
                  className={`zr-court-row ${isExpanded ? "is-expanded" : ""}`}
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", userSelect: "none" }}
                  onClick={() => setExpandedCourts((prev) => ({ ...prev, [court.localId]: !isExpanded }))}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, flexWrap: "wrap" }}>
                    <svg
                      width="12"
                      height="8"
                      viewBox="0 0 12 8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                        transition: "transform 0.2s",
                        color: "var(--zr-ink)"
                      }}
                    >
                      <path d="m1.5 1.5 4.5 4.5 4.5-4.5" />
                    </svg>
                    {isExpanded ? (
                      <input
                        className="zr-input"
                        placeholder="Ej: Cancha 1"
                        value={court.name}
                        onChange={(e) => updateCourt(court.localId, { name: e.target.value })}
                        style={{ width: "100%", maxWidth: "400px", height: "32px", fontSize: "14px", padding: "4px 10px" }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <span style={{ fontSize: "15px", fontWeight: "700", color: "var(--zr-ink)" }}>
                        {court.name || "Cancha sin nombre"}
                      </span>
                    )}
                    {!isExpanded && court.sport && (
                      <span className="zr-badge zr-badge--lime" style={{ fontSize: "10.5px", padding: "2px 8px", textTransform: "capitalize" }}>
                        {SPORT_LABELS[court.sport] || court.sport}
                      </span>
                    )}
                    {!isExpanded && court.courtCategory && (
                      <span className="zr-badge" style={{ fontSize: "10.5px", padding: "2px 8px", background: "#eaedf3", color: "#4b5563" }}>
                        {court.courtCategory}
                      </span>
                    )}
                  </div>
                  <div className="zr-court-row__actions" style={{ display: "flex", alignItems: "center", gap: "10px" }} onClick={(e) => e.stopPropagation()}>
                    {isExpanded && (
                      <>
                        <span className="d-none d-sm-inline" style={{ fontSize: "11.5px", color: "#6b7280", marginRight: "4px" }}>
                          Cambios sin guardar se pierden al salir.
                        </span>
                        <button
                          type="button"
                          className="zr-btn zr-btn--dark"
                          onClick={() => handleSave(court.name)}
                          disabled={saving || loading}
                          style={{ height: "30px", padding: "0 14px", fontSize: "12px", fontWeight: "600", borderRadius: "8px" }}
                        >
                          {saving ? "Guardando..." : "Guardar cambios"}
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="zr-delete-btn"
                      aria-label={`Eliminar ${court.name || "cancha"}`}
                      onClick={() => handleRemoveCourt(court.localId)}
                      style={{ padding: "4px 8px" }}
                    >
                      <svg width="16" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" />
                      </svg>
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ marginTop: 18 }}>
                    <Row className="g-3">
                      <Col md={3}>
                        <div className="zr-field">
                          <span className="zr-field__label">Deporte</span>
                          <OrganizerPillSelect
                            value={court.sport}
                            onChange={(val) => updateCourt(court.localId, { sport: val })}
                            options={clubSportsOptions}
                          />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="zr-field">
                          <span className="zr-field__label">Superficie</span>
                          <OrganizerPillSelect
                            value={court.surface}
                            onChange={(val) => updateCourt(court.localId, { surface: val })}
                            options={SURFACE_OPTIONS}
                          />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="zr-field">
                          <span className="zr-field__label">Techado</span>
                          <OrganizerPillSelect
                            value={court.enclosure}
                            onChange={(val) => updateCourt(court.localId, { enclosure: val })}
                            options={ENCLOSURE_OPTIONS}
                          />
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="zr-field">
                          <span className="zr-field__label">Categoría (opcional)</span>
                          <OrganizerPillSelect
                            value={court.courtCategory}
                            onChange={(val) => updateCourt(court.localId, { courtCategory: val })}
                            options={(() => {
                              const opts = [...categoryOptions];
                              if (court.courtCategory && !opts.some((o) => o.value === court.courtCategory)) {
                                opts.push({ value: court.courtCategory, label: court.courtCategory });
                              }
                              return opts;
                            })()}
                          />
                        </div>
                      </Col>

                      <Col md={3}>
                        <div className="zr-check-row" style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "20px" }}>
                          <input
                            type="checkbox"
                            className="zr-check"
                            checked={court.lighting}
                            onChange={() => updateCourt(court.localId, { lighting: !court.lighting })}
                            style={{ margin: 0, cursor: "pointer", accentColor: "var(--zr-lime)" }}
                          />
                          <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--zr-ink)" }}>Iluminación</span>
                        </div>
                      </Col>
                    </Row>

                    <div style={{ marginTop: 22 }}>
                      <span className="zr-field__label" style={{ display: "block", marginBottom: 8 }}>
                        Precio base por duración
                      </span>
                      <div className="zr-table-wrap" style={{ overflow: "visible" }}>
                        <table className="zr-table zr-table--base-prices">
                          <thead>
                            <tr>
                              <th>Duración</th>
                              <th>Precio</th>
                              <th>Seña</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {court.pricing.defaultByDuration.map((d, i) => (
                              <tr key={i}>
                                <td>
                                  <OrganizerPillSelect
                                    value={String(d.durationMinutes)}
                                    onChange={(val) => handleUpdateDefaultPrice(court.localId, i, { durationMinutes: Number(val) })}
                                    options={DURATION_OPTIONS}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    min={0}
                                    className="zr-input"
                                    value={d.price}
                                    onChange={(e) => handleUpdateDefaultPrice(court.localId, i, { price: e.target.value === "" ? "" : Number(e.target.value) })}
                                  />
                                </td>
                                <td>
                                  <DepositCell
                                    row={d}
                                    rowLabel={`precio base fila ${i + 1}`}
                                    bloqueado={senasBloqueadas}
                                    onChange={(patch) => handleUpdateDefaultPrice(court.localId, i, patch)}
                                  />
                                </td>
                                <td className="right">
                                  <button
                                    type="button"
                                    className="zr-delete-btn"
                                    aria-label="Eliminar precio base"
                                    onClick={() => handleRemoveDefaultPrice(court.localId, i)}
                                  >
                                    <svg width="16" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" />
                                    </svg>
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {court.pricing.defaultByDuration.length === 0 && (
                              <tr>
                                <td colSpan={4} className="muted">
                                  Sin precio base configurado.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <button type="button" className="zr-add-zone" onClick={() => handleAddDefaultPrice(court.localId)} style={{ marginTop: 8 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                        Agregar precio base
                      </button>
                    </div>

                    <div style={{ marginTop: 24, borderTop: "1px dashed #e9edf3", paddingTop: 20 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: 12 }}>
                        <input
                          type="checkbox"
                          className="zr-switch"
                          checked={!!showRulesMap[court.localId]}
                          onChange={() => setShowRulesMap((prev) => ({ ...prev, [court.localId]: !prev[court.localId] }))}
                          style={{ margin: 0 }}
                        />
                        <span className="zr-field__label" style={{ margin: 0, cursor: "pointer" }} onClick={() => setShowRulesMap((prev) => ({ ...prev, [court.localId]: !prev[court.localId] }))}>
                          Reglas de precio por día y horario (opcional)
                        </span>
                      </div>

                      {showRulesMap[court.localId] && (
                        <>
                          <div className="zr-table-wrap" style={{ overflow: "visible", marginTop: 12 }}>
                            <table className="zr-table zr-table--price-rules">
                              <thead>
                                <tr>
                                  <th>Duración</th>
                                  <th>Día</th>
                                  <th>Desde</th>
                                  <th>Hasta</th>
                                  <th>Precio</th>
                                  <th>Seña</th>
                                  <th />
                                </tr>
                              </thead>
                              <tbody>
                                {court.pricing.rules.map((r, i) => (
                                  <tr key={i}>
                                    <td>
                                      <OrganizerPillSelect
                                        value={String(r.durationMinutes)}
                                        onChange={(val) => handleUpdateRule(court.localId, i, { durationMinutes: Number(val) })}
                                        options={DURATION_OPTIONS}
                                      />
                                    </td>
                                    <td>
                                      <OrganizerPillSelect
                                        value={r.weekday === null ? "" : String(r.weekday)}
                                        onChange={(val) => handleUpdateRule(court.localId, i, { weekday: val === "" ? null : Number(val) })}
                                        options={WEEKDAY_OPTIONS}
                                      />
                                    </td>
                                    <td>
                                      <OrganizerTimePillSelect value={r.fromTime} onChange={(val) => handleUpdateRule(court.localId, i, { fromTime: val })} ariaLabel="Desde" triggerClassName="zr-day-row__time" />
                                    </td>
                                    <td>
                                      <OrganizerTimePillSelect value={r.toTime} onChange={(val) => handleUpdateRule(court.localId, i, { toTime: val })} ariaLabel="Hasta" triggerClassName="zr-day-row__time" />
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        min={0}
                                        className="zr-input"
                                        value={r.price}
                                        onChange={(e) => handleUpdateRule(court.localId, i, { price: e.target.value === "" ? "" : Number(e.target.value) })}
                                      />
                                    </td>
                                    <td>
                                      <DepositCell
                                        row={r}
                                        rowLabel={`regla de precio fila ${i + 1}`}
                                        bloqueado={senasBloqueadas}
                                        onChange={(patch) => handleUpdateRule(court.localId, i, patch)}
                                      />
                                    </td>
                                    <td className="right">
                                      <button
                                        type="button"
                                        className="zr-delete-btn"
                                        aria-label="Eliminar regla de precio"
                                        onClick={() => handleRemoveRule(court.localId, i)}
                                      >
                                        <svg width="16" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M4 6.5h16M9 6.5V4.5h6v2M6.5 6.5 7.5 20a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1l1-13.5" />
                                        </svg>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                                {court.pricing.rules.length === 0 && (
                                  <tr>
                                    <td colSpan={7} className="muted">
                                      Sin reglas de precio especiales.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          <button type="button" className="zr-add-zone" onClick={() => handleAddRule(court.localId)} style={{ marginTop: 8 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 5v14M5 12h14" />
                            </svg>
                            Agregar regla de precio
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <button type="button" className="zr-add-zone" onClick={handleAddCourt}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
            Agregar cancha
          </button>
        </div>
      )}



      {toastMessage && (
        <div className="org-toast" role="alert">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default CourtLayoutTab;
